import axios from "axios";
const normalizeBaseURL = (value) => {
  const raw = value || "http://127.0.0.1:8000/api/";
  return raw.endsWith("/") ? raw : `${raw}/`;
};

const baseURL = normalizeBaseURL(process.env.REACT_APP_API_BASE_URL);
const resolveURL = (path) => {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://127.0.0.1:8000";
  return new URL(path, new URL(baseURL, origin)).toString();
};
const refreshURL = resolveURL("accounts/token/refresh/");

const axiosInstance = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});
let isRefreshing = false;
let failedQueue = [];
const TOKEN_REFRESH_SKEW_MS = 60 * 1000;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearStoredTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

const redirectToLogin = () => {
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const getTokenPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

const shouldRefreshAccessToken = (token) => {
  const payload = getTokenPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 - Date.now() <= TOKEN_REFRESH_SKEW_MS;
};

const refreshAccessToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    // The refresh token is an httpOnly cookie. Sessions started before that
    // change still hold one in localStorage; send it once so the server moves
    // it into the cookie.
    const legacyRefreshToken = localStorage.getItem("refresh_token");

    const refreshResponse = await axios.post(
      refreshURL,
      legacyRefreshToken ? { refresh: legacyRefreshToken } : {},
      {
        headers: { "Content-Type": "application/json" },
        skipAuthRefresh: true,
        withCredentials: true,
      },
    );

    const newAccessToken = refreshResponse.data.access;
    localStorage.setItem("access_token", newAccessToken);
    localStorage.removeItem("refresh_token");
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
    processQueue(null, newAccessToken);
    return newAccessToken;
  } catch (refreshError) {
    console.error("Token refresh failed:", refreshError);
    processQueue(refreshError, null);
    clearStoredTokens();
    delete axiosInstance.defaults.headers.common.Authorization;
    redirectToLogin();
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

// Interceptor for setting Authorization header on all requests
axiosInstance.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem("access_token");
    if (!config.skipAuthRefresh && token && shouldRefreshAccessToken(token)) {
      token = await refreshAccessToken();
    }

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    const companyId = localStorage.getItem("active_company_id");
    if (companyId && !config.skipCompany) {
      config.headers = config.headers || {};
      config.headers["X-Company-ID"] = companyId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor for handling responses
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
